import { z } from 'zod';
import { isoDateTimeSchema, uuidSchema } from './common.js';
/**
 * Roles assignable via the **admin** invite endpoint
 * (`POST /admin/settings/admins/invite`). admin-prd §12.2 limits this
 * surface to `admin` and `selector` — super-admin promotion happens on
 * a separate endpoint (out of scope for Wave 2.11).
 *
 * Tenant-side team invites (`client_owner` / `client_manager`) use a
 * different endpoint (`POST /admin/accounts/:id/team/invite`) and the
 * `accountTeamRoleSchema` enum from `account.ts`; both flows funnel into
 * the same `internal.admin_invites` table.
 */
export const adminInviteRoleEnum = z.enum(['admin', 'selector']);
/**
 * Full set of roles `POST /auth/accept-invite` may return on success — a
 * tenant invite resolves to one of the `client_*` roles, an admin invite
 * to `admin` / `selector`. Used by `acceptInviteResponseSchema` below.
 */
export const acceptedInviteRoleEnum = z.enum([
    'admin',
    'selector',
    'client_owner',
    'client_manager',
]);
/**
 * `POST /admin/settings/admins/invite` request body.
 *
 * BUG-023 (Variant 1): admin-panel users are now created from a single
 * endpoint. When `role === 'selector'` the form MUST also supply
 * `payout_rate_per_period` (0..100, integer percent) — this seeds the
 * matching `internal.selectors` row that the unified flow inserts
 * transactionally alongside the user. The discriminated union gives the
 * FE a compile-time guarantee that the field cannot be forgotten on the
 * selector branch.
 */
const adminInviteAdminBranchSchema = z
    .object({
    email: z.string().trim().email().max(255),
    role: z.literal('admin'),
    name: z.string().trim().min(1).max(255),
})
    .strict();
const adminInviteSelectorBranchSchema = z
    .object({
    email: z.string().trim().email().max(255),
    role: z.literal('selector'),
    name: z.string().trim().min(1).max(255),
    payout_rate_per_period: z.number().int().min(0).max(100),
})
    .strict();
export const adminInviteRequestSchema = z.discriminatedUnion('role', [
    adminInviteAdminBranchSchema,
    adminInviteSelectorBranchSchema,
]);
/**
 * `POST /admin/settings/admins/invite` response.
 *
 * `invite_link_dev_only` is populated ONLY in non-production environments so
 * developers can complete the flow without a working SMTP relay. In
 * production the field is undefined; the link goes out via Postmark
 * (TASK-098, later wave).
 */
export const adminInviteResponseSchema = z.object({
    invite_id: uuidSchema,
    expires_at: isoDateTimeSchema,
    invite_link_dev_only: z.string().url().optional(),
});
/**
 * `POST /auth/accept-invite` — PUBLIC (no auth). The invitee activates the
 * account by supplying the raw token + a fresh password.
 *
 * 2FA enrolment happens AFTER the user logs in, via the existing
 * `/auth/2fa/setup` flow — keeping accept-invite single-purpose. A
 * `totp_setup_code` field was rejected here (Wave 2.11 BE-2 review M-1)
 * because the auth service does not read it; adding it would have been
 * contract drift.
 */
export const acceptInviteRequestSchema = z
    .object({
    token: z
        .string()
        .min(32)
        .max(128)
        .regex(/^[0-9a-f]+$/, 'Token must be lowercase hex'),
    password: z.string().min(12).max(255),
})
    .strict();
export const acceptInviteResponseSchema = z.object({
    user_id: uuidSchema,
    email: z.string().email(),
    role: acceptedInviteRoleEnum,
});
//# sourceMappingURL=admin-invite.js.map